/**
 * Tracks the internal page a visitor was on before the current one, so lead forms can
 * tell HubSpot where the visitor came from (e.g. a hire page → contact page).
 *
 * `document.referrer` alone is not enough: Next.js client-side navigation (`next/link`)
 * never updates it, so it would still point at the original external referrer.
 */

const STORAGE_KEY = 'source_page_url';

/** HubSpot contact property that receives the previous page URL. */
export const SOURCE_PAGE_FIELD = 'source_page_url';

/**
 * Sending a field that is missing from a HubSpot form makes the submission fail, so the
 * field is only sent once the property exists and has been added to each form as a
 * hidden field. Set NEXT_PUBLIC_ENABLE_SOURCE_PAGE=true after that is done.
 */
export const isSourcePageEnabled = (): boolean => process.env.NEXT_PUBLIC_ENABLE_SOURCE_PAGE === 'true';

/** Stores the page being left. Call before every client-side route change. */
export const rememberPreviousPage = (url: string): void => {
    try {
        sessionStorage.setItem(STORAGE_KEY, url);
    } catch {
        /* storage blocked (private mode, disabled cookies) — tracking is best-effort */
    }
};

/**
 * The previous internal page, or '' when unknown. Falls back to `document.referrer`
 * for full page loads (new tab, plain anchor), but only when it is our own domain.
 */
export const getSourcePageUrl = (): string => {
    if (globalThis.window === undefined) return '';
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (stored) return stored;
    } catch {
        /* fall through to the referrer */
    }
    return document.referrer.startsWith(globalThis.location.origin) ? document.referrer : '';
};

/** The HubSpot field for the previous page, or none when disabled or unknown. */
export const sourcePageFields = (): {name: string; value: string}[] => {
    if (!isSourcePageEnabled()) return [];
    const value = getSourcePageUrl();
    return value ? [{name: SOURCE_PAGE_FIELD, value}] : [];
};
